import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner'; // Import Spinner for loader
import './Wallet.css';
import apiClient from '../../axios';

// Register chart.js components
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

function WalletPage() {
    const [transactions, setTransactions] = useState([]);
    const [walletInfo, setWalletInfo] = useState({
        amountTaken: 0,
        balance: 0,
    });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false); // Modal state
    const [withdrawAmount, setWithdrawAmount] = useState(''); // Input state
    const [errorMessage, setErrorMessage] = useState('');
    const [isWithdrawLoading, setIsWithdrawLoading] = useState(false); // Withdrawal loader state
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await apiClient.get('/bank/withdrawal/history');
                const data = response.data.data;

                // Map transactions from API response
                const paymentHistory = data.PaymentHistory.map((txn) => ({
                    date: txn.date,
                    description: 'Withdrawal',
                    amount: txn.withdrawal_amount,
                    type: txn.status == 1 ? 'Request sent' : 'Debited',
                }));


                // Update state with API data
                setWalletInfo({
                    amountTaken: data.Userdata[0]?.amount_token || 0,
                    balance: data.Userdata[0]?.wallet_amount || 0,
                });
                setTransactions(paymentHistory);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Handle opening the withdrawal modal
    const handleOpenModal = () => {
        if (walletInfo.balance <= 0) {
            alert('You cannot withdraw because your balance is too low.');
            return;
        }
        setShowModal(true);
    };

    // Handle withdrawal submission
    const handleWithdraw = async () => {
        if (!withdrawAmount || withdrawAmount <= 0) {
            setErrorMessage('Please enter a valid amount.');
            return;
        }
    
        if (withdrawAmount < 1000) {
            setErrorMessage('Withdrawal amount must be greater than ₹1000.');
            return;
        }
    
        if (withdrawAmount > walletInfo.balance) {
            setErrorMessage('Withdrawal amount exceeds your balance.');
            return;
        }

        setIsWithdrawLoading(true); // Show loader during API call
        try {
            const response = await apiClient.post('/bank/amount/withdrawal', { amount: withdrawAmount });
            if (response.data.status == 200) {
                alert('Your request Amount is successful placed');
                setShowModal(false);
                setWithdrawAmount('');
                setErrorMessage('');
                // Refresh data
                window.location.reload();
            } else {
                setErrorMessage('Withdrawal failed. Please try again.');
            }
        } catch (error) {
            console.error('Error during withdrawal:', error);
            setErrorMessage('An error occurred. Please try again.');
        } finally {
            setIsWithdrawLoading(false); // Hide loader after API call
        }
    };

    // Data for the line chart
    const chartData = {
        labels: transactions.map((txn) => txn.date),
        datasets: [
            {
                label: 'Balance Over Time (₹)',
                data: transactions.map((txn) => txn.amount),
                fill: false,
                borderColor: '#007bff',
                tension: 0.1,
            },
        ],
    };

    // Columns for the data table
    const columns = [
        {
            name: 'Request Date',
            selector: (row) => row.date,
            sortable: true,
        },
        {
            name: 'Description',
            selector: (row) => row.description,
            sortable: false,
        },
        {
            name: 'Amount (₹)',
            selector: (row) => `₹${row.amount.toLocaleString()}`,
            sortable: true,
        },
        {
            name: 'Type',
            selector: (row) => row.type,
            sortable: true,
        },
    ];

    return (
        <div className="wallet-page">
            {loading ? (
                <div className="loading-spinner">
                    <p>Loading...</p>
                </div>
            ) : (
                <>
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="wallet-cards mt-5">
                                <div className="wallet-card">
                                    <h2>Amount Taken</h2>
                                    <p>₹{walletInfo.amountTaken.toLocaleString()}</p>
                                </div>
                                <div className="wallet-card">
                                    <h2>Balance</h2>
                                    <p>₹{walletInfo.balance.toLocaleString()}</p>
                                </div>
                                <div className="wallet-card">
                                    <Button className="btn btn-primary mt-5" onClick={handleOpenModal}>
                                        Payment Withdrawal
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="wallet-table">
                                <DataTable
                                    columns={columns}
                                    data={transactions}
                                    pagination
                                    highlightOnHover
                                    fixedHeader
                                    paginationPerPage={5}
                                    fixedHeaderScrollHeight="400px"
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div
                                className=""
                                style={{
                                    backgroundColor: "white",
                                    padding: "5% 30px",
                                    marginTop: "16px",
                                    borderRadius: "10px",
                                }}
                            >

                                {transactions.length > 0 ? (
                                    <>
                                        <h5>Wallet Balance Over Time</h5>
                                        <Line data={chartData} />
                                    </>

                                ) : (
                                    <p className="text-center text-danger" style={{ margin: "0px" }}>
                                        No data available to display on the chart.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Payment Withdrawal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label>Enter Withdrawal Amount:</label>
                        <input
                            type="number"
                            className="form-control"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                        />
                        {errorMessage && <small className="text-danger">{errorMessage}</small>}
                    </div>
                    <p className="mt-3 text-muted">
                        Please note: The requested amount will be transferred to your bank account within one week.
                    </p>
                </Modal.Body>
                <div className="row">
                    <div className="col-lg-6">
                        <button className="w-100 btn btn-secondary" onClick={() => setShowModal(false)}>
                            Close
                        </button>
                    </div>
                    <div className="col-lg-6">
                        <button className="w-100 btn btn-primary" onClick={handleWithdraw} disabled={isWithdrawLoading}>
                            {isWithdrawLoading ? <Spinner animation="border" size="sm" /> : 'Request Withdraw'}
                        </button>
                    </div>
                </div>
            </Modal>

        </div>
    );
}

export default WalletPage;
